/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { projectCreationContentLeftMargin } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { QnaMakerProjectModalFooter } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerProjectModalFooter';
import {
    QnaMakerProjectCreationSteps,
    QnaMakerProjectDto
} from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';

export type ProjectCreationReviewAndFinishProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: QnaMakerProjectDto;
    languageResource: AzureCsAccountStore;
    projectCreationTracker: ProgressTrackerStore;
};

const messages = defineMessages({
    title: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.title',
        defaultMessage: 'Review and finish'
    },
    description: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.description',
        defaultMessage: 'Review the configurations you set for your project in the previous steps.'
    },
    multipleLanguages: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.multipleLanguages',
        defaultMessage: 'Projects in multiple languages allowed?'
    },
    languageResource: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.languageResource',
        defaultMessage: 'Language resource'
    },
    azureSearchResource: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.azureSearchResource',
        defaultMessage: 'Azure Search resource'
    },
    projectName: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.projectName',
        defaultMessage: 'Project name'
    },
    projectDescription: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.projectDescription',
        defaultMessage: 'Description'
    },
    sourceLanguage: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.sourceLanguage',
        defaultMessage: 'Source language'
    },
    defaultAnswer: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.defaultAnswer',
        defaultMessage: 'Default answer when no response is returned'
    },
    labelsFileName: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.labelsFileName',
        defaultMessage: 'Labels file'
    },
    yes: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.yes',
        defaultMessage: 'Yes'
    },
    no: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.no',
        defaultMessage: 'No'
    },
    noValuePlaceholder: {
        id: 'qnaMaker.qnaMakerReviewAndFinishScreen.noValuePlaceholder',
        defaultMessage: 'No value'
    }
});

const { styleComponent } = getStylistV2('ProjectCreationReviewAndFinish');
const ContentWrapper = styleComponent<IStackProps>(Stack)('ContentWrapper', { overflowY: 'auto', maxHeight: '400px' });

export const QnaMakerReviewAndFinishScreen = observer((props: ProjectCreationReviewAndFinishProps) => {
    const { onSubmit, onDismiss, onPrevious, projectDto, languageResource, projectCreationTracker } = props;
    const { localize } = useLocalization();
    const { name, description, language, defaultAnswer, multilingualResource } = projectDto;

    const LabeledText = ({ label, text }: { label: string; text: string }) => (
        <Stack tokens={{ childrenGap: 4 }}>
            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{label}</LuisText>
            <LuisText styles={{ root: { wordWrap: 'break-word' } }}>{text}</LuisText>
        </Stack>
    );

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '500px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${projectCreationContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>

                <ContentWrapper tokens={{ childrenGap: 16 }}>
                    <LabeledText
                        label={localize(messages.multipleLanguages)}
                        text={localize(multilingualResource ? messages.yes : messages.no)}
                    />
                    <LabeledText label={localize(messages.languageResource)} text={languageResource.name || '--'} />
                    <LabeledText
                        label={localize(messages.azureSearchResource)}
                        text={languageResource.ownedSearchResourceInfo.name || '--'}
                    />
                    <LabeledText label={localize(messages.projectName)} text={name || '--'} />
                    <LabeledText label={localize(messages.projectDescription)} text={description || '--'} />
                    <LabeledText
                        label={localize(messages.sourceLanguage)}
                        text={LuisConstants.QnaMaker.SupportedProjectLangues.find(l => l.code === language).name || '--'}
                    />

                    <LabeledText label={localize(messages.defaultAnswer)} text={defaultAnswer || '--'} />
                </ContentWrapper>

                {projectCreationTracker.isFailed && <CollapsedErrorMessage error={projectCreationTracker.error} />}
            </Stack>

            <QnaMakerProjectModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={QnaMakerProjectCreationSteps.ReviewAndFinish}
                onNext={onSubmit}
                isLoading={projectCreationTracker.isRunning}
                isFooterDisabled={projectCreationTracker.isRunning}
            />
        </Stack>
    );
});
